// Asking for time off.
//
// The same form a manager already uses to enter time off directly — the
// difference is entirely in who is filling it in. An owner or manager granting
// it writes time off; anybody else writes a request, and the API decides which
// from the caller's role rather than trusting a flag in the body.
//
// So the copy here has to be honest about which one is happening, because the
// two look identical until somebody doesn't get the day off they thought they
// had booked.

import { useState } from 'react';
import TimeSelect from '../ui/TimeSelect';

export default function RequestTimeOff({ today, isApprover, busy, error, onSubmit, onClose }) {
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [reason, setReason] = useState('');

  const backwards = endDate < startDate;

  function submit(e) {
    e.preventDefault();
    if (backwards) return;
    onSubmit({
      startDate,
      endDate,
      startTime: allDay ? null : startTime,
      endTime: allDay ? null : endTime,
      reason: reason.trim() || null,
    });
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        className="bg-white w-full sm:max-w-lg rounded-t-xl sm:rounded-xl overflow-hidden max-h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Ask for time off"
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-ink-100">
          <div className="min-w-0 flex-1">
            <h2 className="font-medium text-lg leading-snug">
              {isApprover ? 'Mark yourself off' : 'Ask for time off'}
            </h2>
            <p className="text-sm text-ink-500">
              {isApprover
                ? 'This takes effect straight away — you can approve your own.'
                : 'A manager sees this and decides. Nothing changes on the schedule until they do.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-md text-ink-500 hover:bg-ink-100 text-xl leading-none shrink-0"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="flex flex-wrap gap-4">
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1" htmlFor="off-from">
                From
              </label>
              <input
                id="off-from"
                type="date"
                className="input"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  // Dragging the start past the end is a typo, not a request
                  // for a negative range. Move the end rather than complain.
                  if (endDate < e.target.value) setEndDate(e.target.value);
                }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1" htmlFor="off-to">
                To
              </label>
              <input
                id="off-to"
                type="date"
                className="input"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>

          <fieldset>
            <legend className="text-xs font-medium uppercase tracking-wider text-ink-400 mb-1">How much</legend>
            <div className="flex gap-2">
              {[
                [true, 'All day'],
                [false, 'Part of the day'],
              ].map(([value, label]) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => setAllDay(value)}
                  className={
                    'px-3 py-1.5 rounded-full text-sm border ' +
                    (allDay === value
                      ? 'bg-hunter text-white border-hunter'
                      : 'bg-white text-ink-700 border-ink-200 hover:border-ink-300')
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          </fieldset>

          {!allDay && (
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="off-start">From</label>
                <TimeSelect label="From time" value={startTime} onChange={setStartTime} id="off-start" />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1" htmlFor="off-end">To</label>
                <TimeSelect label="To time" value={endTime} onChange={setEndTime} id="off-end" />
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium uppercase tracking-wider text-ink-400 mb-1" htmlFor="off-reason">
              Reason
            </label>
            <input
              id="off-reason"
              className="input w-full"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Away at a show"
            />
            <p className="text-xs text-ink-400 mt-1">
              Optional, but it is what a manager decides on.
            </p>
          </div>

          {backwards && <p className="text-sm text-rose-600">The last day is before the first.</p>}
          {error && <p className="text-sm text-rose-600">{error}</p>}
        </div>

        <div className="px-5 py-4 border-t border-ink-100 flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy || backwards}>
            {busy ? 'Sending…' : isApprover ? 'Mark it off' : 'Send request'}
          </button>
        </div>
      </form>
    </div>
  );
}
